import React, { useState, useRef, useEffect } from 'react';
import { Play } from 'lucide-react';
import { ProfilePhoto } from './types';
import { profileService, MediaAllowance } from '../../services/Profile/profileService';
import {
  uploadToStorage,
  MIME_TO_EXTENSION,
  ALLOWED_IMAGE_TYPES,
  ALLOWED_VIDEO_TYPES,
  MAX_IMAGE_SIZE,
  MAX_VIDEO_SIZE,
} from '../../services/Profile/mediaUpload';
import { captureVideoThumbnail } from '../../utils/media/captureVideoThumbnail';
import { useSubscriptionContext } from '../../providers/SubscriptionProvider';
import {
  PhotoManagerContainer,
  PhotoManagerHeader,
  PhotoManagerTitle,
  PhotoManagerDescription,
  PhotoGrid,
  PhotoSlot,
  PhotoPreview,
  PhotoActions,
  PhotoOrder,
  PhotoValidation,
  UploadButton,
  RemoveButton,
  SetMainButton,
  PhotoTips,
  TipItem,
  ValidationStatus,
  ErrorMessage,
  SuccessMessage,
  PhotoStats,
  StatItem
} from '../../styles/Profile/ProfileEditor.styles';

interface PhotoManagerProps {
  photos: ProfilePhoto[];
  onChange: (photos: ProfilePhoto[]) => void;
  isPremium?: boolean;
  onPremium?: () => void;
  userId: string;
}

// Storage upload, MIME/extension mapping and size limits now live in
// services/Profile/mediaUpload so the onboarding PhotosStep shares them.

// Fallback capacity used only until the server answers with this account's
// real allowance. The free numbers, deliberately: showing a subscriber's
// allowance to everyone for that moment would invite uploads the backend then
// refuses.
const MAX_IMAGES_FREE = 3;
const MAX_VIDEOS_FREE = 1;

const MAX_VIDEO_DURATION = 15; // seconds

export const PhotoManager: React.FC<PhotoManagerProps> = ({
  photos,
  onChange,
  isPremium = false,
  onPremium,
  userId
}) => {
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [allowance, setAllowance] = useState<MediaAllowance | null>(null);
  const { tier } = useSubscriptionContext();
  const imageInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  const images = photos.filter(p => !p.type || p.type === 'image'); // Handle undefined type as image
  const videos = photos.filter(p => p.type === 'video');

  // Capacity comes from the server, which resolves it from the subscription:
  // free 3 images / 1 video, Premium 5 / 3, Premium Plus unlimited. The
  // isPremium prop is a JWT role claim and does not change when someone
  // subscribes, so it is no longer what decides the numbers — only whether to
  // offer the upgrade prompt below.
  // Keyed on tier: upgrading has to raise the visible limits here without a
  // reload, or the editor keeps refusing uploads the account can now make.
  useEffect(() => {
    let cancelled = false;
    profileService.getMediaAllowance()
      .then((next) => { if (!cancelled && next) setAllowance(next); })
      .catch((error) => console.error('Failed to load media allowance:', error));
    return () => { cancelled = true; };
  }, [tier]);

  const maxImages = allowance
    ? (allowance.images.max ?? Infinity)
    : MAX_IMAGES_FREE;
  const maxVideos = allowance
    ? (allowance.videos.max ?? Infinity)
    : MAX_VIDEOS_FREE;
  const hasUnlimitedMedia = allowance?.images.unlimited === true && allowance?.videos.unlimited === true;
  const canUpgrade = !hasUnlimitedMedia;
  const formatMax = (max: number) => (Number.isFinite(max) ? String(max) : '∞');

  const handleMediaUpload = async (files: FileList, type: 'image' | 'video') => {
    const isVideo = type === 'video';
    if (isVideo) setUploadingVideo(true);
    else setUploadingImage(true);

    const newMedia: ProfilePhoto[] = [];
    const currentMediaCount = isVideo ? videos.length : images.length;
    const maxCount = isVideo ? maxVideos : maxImages;

    // Early check for limit
    if (currentMediaCount >= maxCount) {
      if (canUpgrade && onPremium) {
        onPremium();
        if (isVideo) setUploadingVideo(false);
        else setUploadingImage(false);
        return;
      }

      alert(`You can only upload up to ${maxCount} ${type}s`);
      if (isVideo) setUploadingVideo(false);
      else setUploadingImage(false);
      return;
    }

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      // Validate file type
      const allowedTypes = isVideo ? ALLOWED_VIDEO_TYPES : ALLOWED_IMAGE_TYPES;
      if (!allowedTypes.includes(file.type)) {
        alert(`File ${file.name} is not a supported ${type} format`);
        continue;
      }

      // Validate file size
      const maxSize = isVideo ? MAX_VIDEO_SIZE : MAX_IMAGE_SIZE;
      if (file.size > maxSize) {
        alert(`File ${file.name} is too large. Maximum size is ${isVideo ? '50MB' : '10MB'}`);
        continue;
      }

      // Validate video duration
      let durationSeconds: number | undefined;
      if (isVideo) {
        try {
          const duration = await new Promise<number>((resolve, reject) => {
            const video = document.createElement('video');
            video.preload = 'metadata';
            video.style.position = 'fixed';
            video.style.top = '-9999px';
            video.style.width = '1px';
            video.style.height = '1px';
            document.body.appendChild(video);
            video.onloadedmetadata = () => {
              window.URL.revokeObjectURL(video.src);
              video.remove();
              resolve(video.duration);
            };
            video.onerror = () => {
              window.URL.revokeObjectURL(video.src);
              video.remove();
              reject('Failed to load video metadata');
            };
            video.src = URL.createObjectURL(file);
          });

          if (duration > MAX_VIDEO_DURATION) {
            alert(`Video ${file.name} is longer than ${MAX_VIDEO_DURATION} seconds.`);
            continue;
          }
          durationSeconds = Math.round(duration);
        } catch (e) {
          console.error('Video validation error', e);
        }
      }

      // Check limit for batch
      if (currentMediaCount + newMedia.length >= maxCount) {
        if (canUpgrade && onPremium) {
          onPremium(); // Upgrade limit
        } else {
          alert(`You can only upload up to ${maxCount} ${type}s`);
        }
        break;
      }

      try {
        const extension = MIME_TO_EXTENSION[file.type] || (isVideo ? 'mp4' : 'jpg');
        const hostedUrl = await uploadToStorage(userId, file, extension, file.type);

        let thumbnailUrl: string | undefined;
        if (isVideo) {
          const thumbBlob = await captureVideoThumbnail(file);
          if (thumbBlob) {
            thumbnailUrl = await uploadToStorage(userId, thumbBlob, 'jpg', 'image/jpeg');
          }
        }

        const savedPhoto = await profileService.addPhoto(hostedUrl, type, thumbnailUrl, durationSeconds);
        newMedia.push(savedPhoto);
      } catch (error: any) {
        console.error('Error uploading file:', error);
        // The server's message when it has one — the local count can be stale
        // (media added on another device), and 'please try again' would send
        // the user round a loop that cannot succeed.
        alert(error?.code === 'MEDIA_LIMIT_REACHED'
          ? error.message
          : `Failed to upload ${file.name}. Please try again.`);
      }
    }

    if (newMedia.length > 0) {
      // Re-combine all media, keeping images and videos grouped
      const updatedMedia = isVideo
        ? [...images, ...videos, ...newMedia]
        : [...images, ...newMedia, ...videos];

      onChange(updatedMedia);
    }

    if (isVideo) setUploadingVideo(false);
    else setUploadingImage(false);
  };

  const removeMedia = async (id: string) => {
    try {
      await profileService.deletePhoto(userId, id);
      onChange(photos.filter(photo => photo.id !== id));
    } catch (error) {
      console.error('Failed to delete photo:', error);
      alert('Failed to remove media. Please try again.');
    }
  };

  const setMainImage = async (id: string) => {
    try {
      await profileService.setMainPhoto(userId, id);
      const updatedPhotos = photos.map(photo => {
        // Handle legacy photos without type as 'image'
        const photoType = photo.type || 'image';

        if (photoType === 'video') return photo; // Don't touch videos
        return {
          ...photo,
          isMain: photo.id === id
        };
      });
      onChange(updatedPhotos);
    } catch (error) {
      console.error('Failed to set main photo:', error);
      alert('Failed to update main photo. Please try again.');
    }
  };

  const setMainVideo = async (id: string) => {
    try {
      await profileService.setMainPhoto(userId, id);
      const updatedPhotos = photos.map(photo => {
        if (photo.type !== 'video') return photo; // Don't touch images
        return {
          ...photo,
          // isMain, not a separate isMainVideo. The server has one is_main
          // column, now scoped per media type, and it maps to isMain for
          // images and videos alike. The separate flag was set here and read
          // here and nowhere else, so a main video never survived a reload —
          // the badge vanished and the editor offered to set it again.
          isMain: photo.id === id
        };
      });
      onChange(updatedPhotos);
    } catch (error) {
      console.error('Failed to set main video:', error);
      alert('Failed to update main video. Please try again.');
    }
  };

  const renderSlot = (media: ProfilePhoto, index: number) => {
    const isVideo = media.type === 'video';
    const isMain = !!media.isMain;

    return (
      <PhotoSlot key={media.id} $isMain={isMain}>
        <PhotoPreview>
          {isVideo ? (
            media.thumbnail ? (
              <img
                src={media.thumbnail}
                alt={`Video ${index + 1}`}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <video
                src={media.url}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                muted
                playsInline
                onMouseOver={e => (e.target as HTMLVideoElement).play()}
                onMouseOut={e => (e.target as HTMLVideoElement).pause()}
              />
            )
          ) : (
            <img src={media.url} alt={`Media ${index + 1}`} />
          )}

          {isMain && (
            <div className="main-badge">
              {isVideo ? 'Main Video' : 'Main Image'}
            </div>
          )}

          {isVideo && (
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: 'rgba(0,0,0,0.5)',
              borderRadius: '50%',
              padding: '0.5rem',
              display: 'flex'
            }}>
              <Play size={24} color="white" fill="white" />
            </div>
          )}
        </PhotoPreview>

        <PhotoActions>
          {!isMain && (
            <SetMainButton onClick={() => isVideo ? setMainVideo(media.id) : setMainImage(media.id)}>
              Set Main
            </SetMainButton>
          )}

          <RemoveButton onClick={() => removeMedia(media.id)}>
            Remove
          </RemoveButton>
        </PhotoActions>

        <PhotoValidation>
          {media.validationStatus === 'pending' && (
            <ValidationStatus $status="pending">Checking...</ValidationStatus>
          )}
          {media.validationStatus === 'approved' && (
            <ValidationStatus $status="approved"> Approved</ValidationStatus>
          )}
          {media.validationStatus === 'rejected' && (
            <ValidationStatus $status="rejected">Rejected</ValidationStatus>
          )}
        </PhotoValidation>
      </PhotoSlot>
    );
  };

  const imageStats = {
    total: images.length,
    hasMain: images.some(img => img.isMain)
  };

  const videoStats = {
    total: videos.length,
    hasMain: videos.some(vid => vid.isMain)
  };

  return (
    <PhotoManagerContainer>
      <PhotoManagerHeader>
        <PhotoManagerTitle>Manage Media</PhotoManagerTitle>
        <PhotoManagerDescription>
          Upload images and short videos (max 15s) to showcase your personality.
          {canUpgrade && (
            <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#ff6b9d' }}>
              Your plan: {formatMax(maxImages)} images, {formatMax(maxVideos)} videos. <br />
              <button
                onClick={onPremium}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#ff6b9d',
                  textDecoration: 'underline',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  padding: 0
                }}
              >
                Upgrade for more
              </button>
            </div>
          )}
        </PhotoManagerDescription>
      </PhotoManagerHeader>

      {/* --- IMAGES SECTION --- */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={{ fontSize: '1.2rem', fontWeight: 600, marginBottom: '1rem', display: 'flex', justifyContent: 'space-between' }}>
          Profile Images
          <span style={{ fontSize: '0.9rem', fontWeight: 400, color: '#666' }}>
            {imageStats.total} / {formatMax(maxImages)}
          </span>
        </h3>

        <PhotoGrid>
          {images.map((img, idx) => renderSlot(img, idx))}
        </PhotoGrid>

        <input
          ref={imageInputRef}
          type="file"
          multiple
          accept={ALLOWED_IMAGE_TYPES.join(',')}
          onChange={(e) => e.target.files && handleMediaUpload(e.target.files, 'image')}
          style={{ display: 'none' }}
        />

        {images.length < maxImages && (
          <UploadButton
            onClick={() => imageInputRef.current?.click()}
            disabled={uploadingImage}
          >
            {uploadingImage ? 'Uploading...' : 'Add Images'}
          </UploadButton>
        )}
        {images.length >= maxImages && canUpgrade && (
          <UploadButton
            onClick={onPremium}
            style={{ backgroundColor: '#ff6b9d' }}
            disabled={uploadingImage}
          >
            Upgrade to add more images
          </UploadButton>
        )}
      </div>

      <hr style={{ margin: '2rem 0', opacity: 0.2 }} />

      {/* --- VIDEOS SECTION --- */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={{ fontSize: '1.2rem', fontWeight: 600, marginBottom: '1rem', display: 'flex', justifyContent: 'space-between' }}>
          Profile Videos (Max 15s)
          <span style={{ fontSize: '0.9rem', fontWeight: 400, color: '#666' }}>
            {videoStats.total} / {formatMax(maxVideos)}
          </span>
        </h3>

        <PhotoGrid>
          {videos.map((vid, idx) => renderSlot(vid, idx))}
        </PhotoGrid>

        <input
          ref={videoInputRef}
          type="file"
          multiple
          accept={ALLOWED_VIDEO_TYPES.join(',')}
          onChange={(e) => e.target.files && handleMediaUpload(e.target.files, 'video')}
          style={{ display: 'none' }}
        />

        {videos.length < maxVideos && (
          <UploadButton
            onClick={() => videoInputRef.current?.click()}
            disabled={uploadingVideo}
          >
            {uploadingVideo ? 'Uploading...' : 'Add Videos'}
          </UploadButton>
        )}
        {videos.length >= maxVideos && canUpgrade && (
          <UploadButton
            onClick={onPremium}
            style={{ backgroundColor: '#ff6b9d' }}
            disabled={uploadingVideo}
          >
            Upgrade to add more videos
          </UploadButton>
        )}
      </div>

      <PhotoTips>
        <h4>Media Tips:</h4>
        <TipItem>✓ Main image should clearly show your face</TipItem>
        <TipItem>✓ Videos must be 15 seconds or less</TipItem>
        <TipItem>✓ Use high quality media to get more matches</TipItem>
      </PhotoTips>

    </PhotoManagerContainer>
  );
};
